import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ContentLoading, ErrorPage } from 'axway-react-ui';
import * as actions from '../actions';
import EndpointsView from '../view/EndpointsView';
import SpecView from '../view/SpecView';
import DynamicOpenAPIView from '../view/DynamicOpenAPIView';
import DeleteEndpointDialog from '../view/DeleteEndpointDialog';
import APIImport from './APIImport';

export class EndpointsContainer extends React.Component {
	constructor(props) {
		super(props);
		this.deleteEndpoint = this.deleteEndpoint.bind(this);
		this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
		this.confirmDeleteEndpoint = this.confirmDeleteEndpoint.bind(this);
		this.onTestAPIExecute = this.onTestAPIExecute.bind(this);
		this.onTestAPIClear = this.onTestAPIClear.bind(this);
		this.onTestAPICancel = this.onTestAPICancel.bind(this);
		this.onUpdateSpec = this.onUpdateSpec.bind(this);
		this.onDeleteSpec = this.onDeleteSpec.bind(this);
		this.onDeleteSpecComplete = this.onDeleteSpecComplete.bind(this);
	}

	onTestAPIExecute(endpointId, verb, path, request) {
		const methodId = `${verb}_${path}`;
		request.uri = `${request.uri}`;
		this.props.actions.testAPIEndpoint(endpointId, methodId, request);
	}

	onTestAPIClear(endpointId, verb, path) {
		const methodId = `${verb}_${path}`;
		this.props.actions.testAPIEndpointClear(endpointId, methodId);
	}

	onTestAPICancel(endpointId, verb, path) {
		const methodId = `${verb}_${path}`;
		this.props.actions.testAPIEndpointCancel(endpointId, methodId);
	}

	onUpdateSpec(type) {
		this.props.actions.importApi(type);
	}

	onDeleteSpec(triggerTypeId) {
		this.props.actions.deleteSpec(triggerTypeId);
	}

	onDeleteSpecComplete() {
		this.props.actions.completeDeleteSpec();
	}

	render() {
		const {
			error,
			errorDetail,
			endpointToDelete,
			endpoint,
			triggerTypeId,
			triggeredFlows,
			invalidTriggers,
			swagger,
			name,
			testapi,
			endpointId,
			systemConfig,
			project,
			type,
			updateDetail,
			apiSpecTypes,
			deleteSpecError,
			url
		} = this.props;

		if (error) {
			return (
				<ErrorPage errorDetail={errorDetail} />
			);
		} else if (updateDetail) {
			return (
				<APIImport
					currentSpec={endpoint}
					specId={endpointId}
				/>
			);
		} else if (project && (endpoint && type === 'endpoint' || endpointToDelete)) {
			return (
				<div>
					<DeleteEndpointDialog
						endpointToDelete={endpointToDelete}
						deleteEndpoint={this.deleteEndpoint}
						closeDialog={this.closeDeleteDialog}
					/>
					<EndpointsView
						endpoint={endpoint}
						swagger={swagger}
						url={url}
						prefix={systemConfig.apiPrefix}
						endpointId={endpointId}
						testapi={testapi}
						project={project}
						confirmDeleteEndpoint={this.confirmDeleteEndpoint}
						onTestAPIExecute={this.onTestAPIExecute}
						onTestAPICancel={this.onTestAPICancel}
						onTestAPIClear={this.onTestAPIClear}
					/>
				</div>
			);
		} else if (project && endpoint && type === 'dynamic') {
			return (
				<DynamicOpenAPIView
					spec={endpoint}
					url={url}
					auth={{
						apikey: project.apikey,
						apiPrefixSecurity: project.apiPrefixSecurity
					}}
				/>
			);
		} else if (project && endpoint) {
			return (
				<SpecView
					spec={endpoint}
					url={url}
					specType={type}
					specTypeName={apiSpecTypes[type].name}
					specName={name}
					auth={{
						apikey: project.apikey,
						apiPrefixSecurity: project.apiPrefixSecurity
					}}
					triggerTypeId={triggerTypeId}
					triggeredFlows={triggeredFlows}
					invalidTriggers={invalidTriggers}
					onUpdate={this.onUpdateSpec}
					onDelete={this.onDeleteSpec}
					onDeleteComplete={this.onDeleteSpecComplete}
					deleteSpecError={deleteSpecError}
				/>
			);
		}
		return <ContentLoading />;
	}

	/**
	 * fires the action to send delete request to arrow-admin-api
	 */
	deleteEndpoint() {
		// could get this out with yield select, but easier as action
		this.props.actions.deleteEndpoint({
			endpointId: this.props.endpointId,
			endpointToDelete: this.props.endpointToDelete
		});
	}

	/**
	 * closes the delete dialog modal bey nulling endpointToDelete
	 */
	closeDeleteDialog() {
		this.props.actions.confirmDeleteEndpoint({
			endpointId: this.props.endpointId,
			endpointToDelete: null
		});
	}

	/**
	 * shows the confirmation dialog by setting endpointToDelete
	 * @param  {item} item this is a row item for that endpoint
	 */
	confirmDeleteEndpoint(item) {
		this.props.actions.confirmDeleteEndpoint({
			endpointId: this.props.endpointId,
			endpointToDelete: item
		});
	}
}

EndpointsContainer.propTypes = {
	endpointId: PropTypes.string.isRequired
};

function mapStateToProps(state, ownProps) {
	const endpointId = ownProps.match.params.id;
	const endpointState = state.apidocs.endpoints[endpointId];

	return {
		endpointId,
		endpoint: endpointState && endpointState.endpoint,
		name: endpointState && endpointState.name,
		triggerTypeId: endpointState && endpointState.triggerTypeId,
		triggeredFlows: endpointState && endpointState.triggeredFlows,
		invalidTriggers: endpointState && endpointState.invalidTriggers,
		type: endpointState && endpointState.type,
		url: endpointState && endpointState.url,
		error: endpointState && endpointState.error,
		errorDetail: endpointState && endpointState.errorDetail,
		updateDetail: state.apidocs && state.apidocs.importDetail,
		apiSpecTypes: state.apidocs.apiSpecTypes.types,
		deleteSpecError: state.apidocs.deleteSpecError,
		// the following props are only used by legacy endpoint UI
		endpointToDelete: endpointState && endpointState.endpointToDelete,
		swagger: endpointState && endpointState.swagger,
		systemConfig: state.system.config,
		testapi: (state.apidocs.testapi && state.apidocs.testapi[endpointId]) || {},
		project: state.project.project
	};
}

function mapDispatchToProps(dispatch) {
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(EndpointsContainer);
